import { useState } from 'react';

import { useAtom } from 'jotai';
import { GetServerSideProps, GetServerSidePropsResult } from 'next';
import { SSRConfig } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { BiStore } from 'react-icons/bi';
import { FaShapes } from 'react-icons/fa';
import {
  HiCollection,
  HiGlobeAlt,
  HiLocationMarker,
  HiPhone,
} from 'react-icons/hi';
import { useEffectOnce } from 'usehooks-ts';

import { GetAdsByUser, getCountAds } from '@/api/fetch/marketplace-api';
import { getUser } from '@/api/fetch/rest-marketplace-api';
import { rudderstackTrackEvent } from '@/api/rudderstack/client';
import { APP_CONFIG } from '@/app-config';
import { LIMIT } from '@/lib/constants';
import { getSubCategoryById } from '@/lib/helpers';
import { stickyBannerAtom } from '@/lib/store';
import { cleanObject } from '@/lib/utils';
import Layout from '@/modules/core/components/Layout';
import ShopListings from '@/modules/core/components/ShopListings';
import { mapUserResponseToIUserData } from '@/modules/core/mappings';
import {
  FilterPrivateAdsType,
  IListingItem,
  IUserData,
} from '@/modules/core/types';
import { Meta } from '@/seo/Meta';
import Pattern from '@/tayara-kit/Pattern';
import Text from '@/tayara-kit/Text';

interface ShopPublicPageProps extends SSRConfig {
  shopData: IUserData;
  countAdByShop: number;
  adsByShop: IListingItem[];
}

const ShopPublicPage = ({
  shopData,
  countAdByShop,
  adsByShop,
}: ShopPublicPageProps) => {
  const [showPhoneNumber, setshowPhoneNumber] = useState(false);
  useEffectOnce(() => {
    rudderstackTrackEvent('page_view', { page_type: `shop/${shopData?.id}` });
  });

  const iqosShopId = shopData.id;

  const social = [
    {
      label: 'Site web',
      icon: HiGlobeAlt,
      link: shopData.url,
    },
    // {
    //   label: 'Facebook',
    //   icon: BsFacebook,
    //   link: '#',
    // },
    // {
    //   label: 'Partager',
    //   icon: FaShare,
    //   link: '#',
    // },
    // {
    //   label: 'Signaler',
    //   icon: HiFlag,
    //   link: '#',
    // },
  ];

  const togglePhoneNumber = () => {
    setshowPhoneNumber((p) => !p);
  };

  const shopCover = shopData.shopBackgroundIMage || '';
  const shopAvatar = shopData.avatar || '';
  const [stickyBar] = useAtom(stickyBannerAtom);

  return (
    <div className={` lg:mt-20 ${stickyBar ? 'mt-36' : 'mt-20'}`}>
      {/* shops cover photo */}
      <div className="w-full relative">
        {shopCover ? (
          <img
            src={
              shopCover?.includes('http')
                ? `https://www.tayara.tn/mediaGateway/resize-image?img=${shopCover.substring(
                    shopCover.lastIndexOf('/') - 2
                  )}${iqosShopId ? '&w=2000' : '&w=1000'}`
                : `https://www.tayara.tn/mediaGateway/resize-image?img=${shopCover}${
                    iqosShopId ? '&w=2000' : '&w=1000'
                  }`
            }
            className="w-full aspect-cover-handset md:aspect-cover-desktop object-cover"
            alt={`tayara shop cover of ${shopData.fullname}`}
            onError={() => {}}
          />
        ) : (
          <div className=" w-full aspect-cover-handset md:aspect-cover-desktop object-cover bg-gradient-to-br bg-blue-400 from-black/30 to-black/50 text-white/30">
            <Pattern />
          </div>
        )}

        <div className="avatar absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2  border-b-[1px] border-gray-500 rounded-[3.5rem]">
          <div className="z-[-1] bg-white w-22 lg:w-40 absolute mask mask-squircle "></div>
          <div className="w-20 lg:w-36 m-1 lg:m-2 mask mask-squircle">
            {shopData.avatar ? (
              <img
                src={
                  shopAvatar?.includes('http')
                    ? `https://www.tayara.tn/mediaGateway/resize-image?img=${shopAvatar.substring(
                        shopAvatar.lastIndexOf('/') - 2
                      )}${iqosShopId ? '&w=250' : '&w=100'}`
                    : `https://www.tayara.tn/mediaGateway/resize-image?img=${shopAvatar}${
                        iqosShopId ? '&w=250' : '&w=100'
                      }`
                }
                className="w-70 mx-auto"
                alt={`tayara shop avatar of ${shopData.fullname}`}
              />
            ) : (
              <BiStore
                size="100%"
                className="w-full text-blue-100 bg-blue-400 p-4 flex"
              />
            )}
          </div>
        </div>

        {/* Boutique tayara logo */}
        <div className="absolute bottom-0 right-0 w-28 mr-3 mb-3 py-2 px-3 overflow-hidden rounded-md backdrop-filter ">
          <img
            src="/media/boutique-tayara.svg"
            className=""
            alt={`tayara shop avatar`}
          />
        </div>
      </div>

      {/* Metadata and social */}
      <div className="md:h-20 mt-20 md:mt-0 gap-3 md:gap-48 flex flex-col md:grid grid-cols-2 pt-2">
        <div className="flex gap-2 items-end mx-auto md:mx-0">
          {social.map(
            (item) =>
              item.link && (
                <a
                  target="_blank"
                  key={item.label}
                  href={item.link}
                  className="w-full p-2 hover:no-underline cursor-pointer border-none flex justify-end text-gray-700 font-arabic"
                  rel="noreferrer"
                >
                  <span className="flex flex-col gap-1 items-center text-gray-600 bg-transparent rounded-md py-2 px-1 hover:text-blue-600 hover:bg-blue-100 cursor-pointer">
                    <item.icon size={22} />
                    <Text text={item.label} className="text-xs" />
                  </span>
                </a>
              )
          )}
        </div>
        <div className="flex flex-col justify-center items-center md:items-start lg:items-start ">
          <span className=" text-gray-700 flex items-center text-2xs gap-2">
            <FaShapes size={13} className="text-gray-600" />
            <Text
              text={
                getSubCategoryById(shopData.categoryId) &&
                getSubCategoryById(shopData.categoryId).name
              }
            />
          </span>
          {shopData.address && (
            <span className="text-gray-700 flex justify-center md:justify-start lg:justify-start items-center my-1 lg:my-0 text-2xs gap-2">
              <HiLocationMarker
                size={13}
                className="text-gray-600 self-start lg:self-center mt-1 lg:mt-0"
              />
              <Text
                className="w-[80%] text-center md:text-start lg:text-start"
                text={shopData.address || shopData.location || ''}
              />
            </span>
          )}
          <span className="text-gray-700 flex items-center  text-2xs gap-2">
            <HiCollection size={13} className="text-gray-600" />
            <Text text={`${countAdByShop} annonces`} />
          </span>
        </div>
      </div>

      {/* Shop name and trusted tag */}
      <div className="flex justify-center mt-8">
        <span className="flex items-center gap-2 relative">
          <Text
            text={shopData.fullname?.toLowerCase() || ''}
            className="text-gray-700 font-bold text-2xl capitalize"
          />
          {/* <HiShieldCheck
            size={24}
            className="text-teal-500 absolute -right-2 mt-0.5 translate-x-full"
          /> */}
        </span>
      </div>

      {/* Shop description and contact */}
      <div className="flex justify-center mt-6">
        <div className="w-full lg:w-1/2 flex flex-col items-center gap-4">
          {/* actions */}
          <div className="w-full flex flex-col ">
            {/* <button className="btn btn-sm rounded-md normal-case btn-outline btn-info space-x-1">
              <IoChatbox size={18} /> <Text text="Chat avec le vendeur" />
            </button> */}
            <button
              aria-label="Afficher le téléphone"
              onClick={togglePhoneNumber}
              className="btn text-xs self-center w-fit btn-sm rounded-md normal-case btn-outline btn-info space-x-1"
            >
              <HiPhone size={18} />{' '}
              <Text
                text={
                  showPhoneNumber
                    ? shopData.phonenumber || ''
                    : 'Afficher le téléphone'
                }
              />
            </button>
          </div>
          <div className="w-fit">
            <p className="lg:text-sm text-xs text-center">
              {shopData.description || 'pas de description'}
            </p>
          </div>
        </div>
      </div>
      <div className="mt-6 mx-0 lg:mx-4">
        <ShopListings
          adsByShop={adsByShop}
          shopData={shopData}
          countAdByShop={countAdByShop}
        />
      </div>
    </div>
  );
};

export default ShopPublicPage;

// Layout configuration
ShopPublicPage.getLayout = function getLayout(page: JSX.Element) {
  return (
    <Layout>
      <Meta
        description="page boutique"
        title={`${APP_CONFIG.site_name} | page boutique`}
      />
      {page}
    </Layout>
  );
};

// Server side code and i18n
export const getServerSideProps: GetServerSideProps = async ({
  locale,
  query,
  res,
}): Promise<GetServerSidePropsResult<ShopPublicPageProps>> => {
  const { shopId } = query;

  const localeData = await serverSideTranslations(locale || 'en');

  if (typeof shopId !== 'string' || !shopId) {
    res.statusCode = 400;
    return {
      notFound: true,
    };
  }

  const filterParams: FilterPrivateAdsType = {
    userId: shopId,
    isPublic: true,
    limit: LIMIT,
    skip: 0,
    status: 2,
    category: '',
  };
  const [shop, adsByShop, countAdByShop] = await Promise.all([
    getUser(shopId),
    GetAdsByUser(filterParams),
    getCountAds(filterParams),
  ]);

  const serializedShop = mapUserResponseToIUserData(shop);

  // TO BE Fixed categoryId msut be id not name
  // let category = {};
  // if (serializedShop.categoryId) {
  //   const categoryByIdRequest = new CategoryByIDRequest();
  //   categoryByIdRequest.setId(serializedShop.categoryId as string);

  //   category = await getCategory(categoryByIdRequest);
  // }
  return {
    props: cleanObject({
      ...localeData,
      shopData: serializedShop,
      countAdByShop: countAdByShop.getCount(),
      adsByShop,
    }),
  };
};
